import { z } from 'zod/mini';
import { OrderLineGroupSchema, OrderLineSchema } from '~/validation/api/packageSessionResponse';
import OptionRuleGroupInterface from './optionRuleGroupInterface';

/**
 * Returns an array of booleans that define what paxes cannot be selected anymore.
 * So for example, if order line 1 (e-bike) has pax #1 on a quantity of 1,
 * we cannot select pax #1 for order line 2 (hybrid bike) anymore.
 */
const disabledPaxes = (
  currentOrderLine: z.infer<typeof OrderLineSchema>,
  groupOrderLines: z.infer<typeof OrderLineSchema>[],
  pax: number,
) => {
  // We start with an array of all paxes, by default we say
  // "the user can still select every pax" (none is disabled)
  const paxesDisabled: boolean[] = Array(pax).fill(false);

  // Get an array of all other order lines in the group, so if the group
  // has e-bike and hybrid bike. But we're currently checking the
  // hybrid bike, the other order lines are the e-bike.
  const otherOrderLinesInGroup = groupOrderLines
    .filter((otherOrderLine) => otherOrderLine.id !== currentOrderLine.id);

  // If the current order line is not per pax (select all/none). We've to
  // disable the order line, if there is a pax selected in any of the other
  // order lines.
  if (!currentOrderLine.per_pax) {
    for (
      let otherOrderLineIndex = 0;
      otherOrderLineIndex < otherOrderLinesInGroup.length;
      otherOrderLineIndex += 1
    ) {
      const otherOrderLine = otherOrderLinesInGroup[otherOrderLineIndex];
      const hasPaxSelected = otherOrderLine.quantities.some((quantity) => quantity > 0);

      if (hasPaxSelected) {
        return Array(pax).fill(true);
      }
    }
  }

  // Loop over every pax and check if the pax has a quantity > 0 in any
  // of the other order lines in the group. If so, we disable that pax.
  for (let paxIndex = 0; paxIndex < pax; paxIndex += 1) {
    otherOrderLinesInGroup.forEach((otherOrderLine) => {
      if (otherOrderLine.quantities[paxIndex] > 0) {
        paxesDisabled[paxIndex] = true;
      }
    });
  }

  return paxesDisabled;
};

export default class MutuallyExclusive implements OptionRuleGroupInterface {
  // eslint-disable-next-line class-methods-use-this
  apply(
    orderLines: z.infer<typeof OrderLineSchema>[],
    _defaultOrderLines: z.infer<typeof OrderLineSchema>[],
    group: z.infer<typeof OrderLineGroupSchema>,
    pax: number,
  ): z.infer<typeof OrderLineSchema>[] {
    const groupOrderLines = orderLines
      .filter((orderLine) => group.order_lines.includes(orderLine.id));

    return orderLines.map((orderLine) => {
      // If this order line is not in the group, we don't need to do anything.
      if (!group.order_lines.includes(orderLine.id)) {
        return orderLine;
      }

      const orderLineClone = { ...orderLine };

      const paxesDisabled = disabledPaxes(orderLine, groupOrderLines, pax);

      orderLineClone.pax_disabled = paxesDisabled;

      // Enable the order line by default
      orderLineClone.disabled = false;

      // Reset the quantities to 0 if the pax is disabled
      orderLineClone.quantities = orderLine.quantities.map((quantity, index) => {
        if (paxesDisabled[index]) {
          return 0;
        }

        return quantity;
      });

      // If all paxes are disabled, disable the whole order line
      const allPaxesDisabled = paxesDisabled.every((paxFilled) => !!paxFilled);

      if (allPaxesDisabled) {
        orderLineClone.disabled = true;
        orderLineClone.quantities = Array(pax).fill(0);
      }

      return orderLineClone;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  errors(
    orderLines: z.infer<typeof OrderLineSchema>[],
    defaultOrderLines: z.infer<typeof OrderLineSchema>[],
    group: z.infer<typeof OrderLineGroupSchema>,
    _pax: number,
  ): string[] {
    if (group.type !== 'mutually_exclusive_mandatory') {
      return [];
    }

    // E.g. "Bring your own bike"
    const groupDefaultOrderLines = defaultOrderLines
      .filter((orderLine) => group.order_lines.includes(orderLine.id));

    // If the group has a default order line, we don't need to check further.
    // If you don't select other option, the default will be in effect.
    if (groupDefaultOrderLines.length) {
      return [];
    }

    // Order lines in the group, e.g. "Hybrid bike" and "E-bike"
    const groupOrderLines = orderLines
      .filter((orderLine) => group.order_lines.includes(orderLine.id));

    // Get the first order line that has a quantity > 0
    const firstSelectedOption = groupOrderLines
      .find((option) => option.quantities.some((quantity) => quantity > 0));

    // If there's no order line with a value, return an error
    if (!firstSelectedOption) {
      return [group.name];
    }

    return [];
  }
}
